import { useNavigate } from '@tanstack/react-router'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  TextField,
} from '@/components/ui'
import { useToast } from '@/hooks'
import {
  useDialogActions,
  useDialogData,
  useDialogIsOpen,
  useForm,
} from '@/stores'
import { getFolderPath } from '@/utils'
import { useFoldersMutating, useFoldersMutations } from '../../hooks'
import type { FolderKind } from '../../stores/types'

const dialogId = 'create-folder'

interface DialogData {
  kind: FolderKind
}

interface FolderFormData {
  name?: string
}

export const CreateFolderDialog = () => {
  const { successToast, errorToast } = useToast()
  const navigate = useNavigate()
  const open = useDialogIsOpen(dialogId)
  const { kind } = (useDialogData() as DialogData) ?? {}
  const { closeDialog } = useDialogActions()
  const { data, errors, setFieldError, setFieldValue } = useForm()
  const { createFolder } = useFoldersMutations()
  const isCreating = useFoldersMutating()

  const entityLabel = kind === 'document' ? 'documents' : 'projects'

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const { name } = data as FolderFormData

    if (!name || name.trim().length === 0) {
      setFieldError('name', 'Name is required')
      return
    }

    createFolder({ name, kind })
      .then((folder) => {
        closeDialog(true)
        successToast('Created folder successfully')
        navigate({ to: getFolderPath(folder) })
      })
      .catch((error) => errorToast('Failed to create folder', error))
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create new folder</DialogTitle>
          <DialogDescription>
            Add a new folder to organize your {entityLabel}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit}>
          <div className="py-4 space-y-4">
            <div className="space-y-6">
              <TextField
                label="Folder Name"
                id="folder-name"
                placeholder="Enter folder name"
                value={data?.name as string}
                error={errors?.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('name', e.target.value)
                }
                required
                autoFocus
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button theme="outline" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isCreating}>
              Create Folder
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

CreateFolderDialog.id = dialogId
